import { IDropdownOption, Toggle } from '@fluentui/react';
import {
  camelCaseToTitleCase,
  Dropdown,
  FieldType,
  FileUpload,
  Info,
  infoStylesNoSpace,
  Link,
  linkStylesGhost,
  TextField,
  useClassNames,
} from '@h2oai/ui-kit';
import { Fragment, useCallback, useState } from 'react';

import {
  Credentials,
  DerivedInformation,
  RawDataLocation,
  TransformationType,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { DataSourceFileInfo, dataSourceUploadFileFieldMap, TransformationInfo } from '../../featurestore/models';
import { getMd5Checksum } from '../../featurestore/utils';
import { FeatureSetJoin } from '../FeatureSetJoin/FeatureSetJoin';
import { FeatureSetTransformation } from '../FeatureSetTransformation/FeatureSetTransformation';
import {
  ISelectDataSourceClassNames,
  ISelectDataSourceStyles,
  selectDataSourceStylesDefault,
  toggleStylesSelectDataSource,
} from './SelectDataSource.styles';

export interface DataSourceInfo {
  cred?: Credentials;
  dataSource?: RawDataLocation;
  derivedFrom?: DerivedInformation;
  tempTransformationInfo?: TransformationInfo;
  tempDataSourceFileInfo?: DataSourceFileInfo;
}

const getEmptyValues = (): DataSourceInfo => {
  return {
    cred: undefined,
    dataSource: undefined,
  };
};

enum CredentialType {
  aws = 'AWS',
  minio = 'Minio',
  azure = 'Azure',
  snowflake = 'Snowflake',
  jdbcDatabase = 'JDBC',
  drive = 'Drive',
}

enum AzureCredentialType {
  key = 'Key',
  SAS = 'SAS',
  serviceProvider = 'Service Provider',
}

// onlineSource is used internally only.
enum RawDataLocationType {
  csv = 'CSV',
  parquet = 'Parquet',
  snowflake = 'Snowflake',
  jdbc = 'JDBC Table',
  json = 'JSON File',
  deltaTable = 'Delta Table',
  csvFolder = 'CSV Folder',
  parquetFolder = 'Parquet Folder',
  jsonFolder = 'JSON Folder',
  // onlineSource = 'Online Source',
  // *** derived feature set ***
  join = 'Join',
  sparkpipelineTransformation = 'Spark Pipeline Transformation',
  mojoTransformation = 'Mojo Transformation',
}

enum DeltaTableFilterType {
  noFilter = 'No Filter',
  text = 'Textual Filter',
  numeric = 'Numerical Filter',
  boolean = 'Boolean Filter',
}

enum ChangedFieldType {
  showFileUpload = 'showFileUpload',
  deltaTableFilter = 'deltaTableFilter',
  files = 'files',
}

interface ChangedFieldValue {
  deltaTableFilter?: DeltaTableFilterType;
  showFileUpload?: boolean;
  parents?: string[];
  files?: File[];
  name?: string;
}

const credentialTypeFieldNameMap = new Map<CredentialType, string>([
  [CredentialType.aws, 'aws'],
  [CredentialType.minio, 'aws'],
  [CredentialType.azure, 'azure'],
  [CredentialType.snowflake, 'snowflake'],
  [CredentialType.jdbcDatabase, 'jdbcDatabase'],
  [CredentialType.drive, 'drive'],
]);

const credentialTypeInfoMap = new Map([
  [
    CredentialType.aws,
    {
      name: credentialTypeFieldNameMap.get(CredentialType.aws),
      fields: ['accessToken', { name: 'secretToken', type: 'password' }, 'sessionToken', 'region'],
    },
  ],
  [
    CredentialType.minio,
    {
      name: credentialTypeFieldNameMap.get(CredentialType.minio),
      fields: ['accessToken', { name: 'secretToken', type: 'password' }, 'region', 'endpoint'],
    },
  ],
  [
    CredentialType.azure,
    {
      name: credentialTypeFieldNameMap.get(CredentialType.azure),
      fields: [
        'accountName',
        {
          title: 'Azure credentials',
          name: 'azureCredential',
          fields: [
            {
              name: AzureCredentialType.key,
              fields: [{ name: 'accountKey', type: 'password' }],
            },
            {
              name: AzureCredentialType.SAS,
              fields: [{ name: 'sasToken', type: 'password' }, 'sasContainer'],
            },
            {
              name: AzureCredentialType.serviceProvider,
              fields: ['spClientId', 'spTenantId', { name: 'spSecret', type: 'password' }],
            },
          ],
        },
      ],
    },
  ],
  [
    CredentialType.snowflake,
    {
      name: credentialTypeFieldNameMap.get(CredentialType.snowflake),
      fields: [
        'user',
        { name: 'password', type: 'password' },
        {
          title: 'Snowflake Use Private Key',
          fields: [
            { name: 'pemPrivateKey', multiline: true },
            { name: 'passphrase', type: 'password' },
          ],
        },
      ],
    },
  ],
  [
    CredentialType.jdbcDatabase,
    {
      name: credentialTypeFieldNameMap.get(CredentialType.jdbcDatabase),
      fields: ['user', { name: 'password', type: 'password' }],
    },
  ],
]);

const rawDataLocationTypeInfoMap = new Map([
  [
    RawDataLocationType.csv,
    {
      name: 'csv',
      fields: [{ name: 'path', info: 'example: s3a://feature-store-test-data/training.csv' }, 'delimiter'],
    },
  ],
  [
    RawDataLocationType.parquet,
    { name: 'parquet', fields: [{ name: 'path', info: 'example: s3a://feature-store-test-data/training.parquet' }] },
  ],
  [
    RawDataLocationType.snowflake,
    {
      name: 'snowflake',
      fields: [
        'table',
        'database',
        'url',
        'query',
        'warehouse',
        'schema',
        { name: 'insecure', fieldType: FieldType.toggle },
        'role',
        'account',
        {
          title: 'Proxy',
          name: 'proxy',
          fields: ['host', { name: 'port', type: 'number' }, 'user', { name: 'password', type: 'password' }],
        },
      ],
    },
  ],
  [
    RawDataLocationType.jdbc,
    {
      name: 'jdbc',
      fields: [
        'table',
        'query',
        'connectionUrl',
        { name: 'numPartitions', type: 'number' },
        'partitionColumn',
        'lowerBound',
        'upperBound',
        { name: 'fetchSize', type: 'number' },
      ],
    },
  ],
  [
    RawDataLocationType.json,
    {
      name: 'json',
      fields: [
        { name: 'path', info: 'example: s3a://feature-store-test-data/training.json' },
        { name: 'multiline', fieldType: FieldType.toggle },
      ],
    },
  ],
  [
    RawDataLocationType.deltaTable,
    {
      name: 'deltaTable',
      fields: [
        { name: 'path', info: 'example: s3a://feature-store-test-data/delta_source_data' },
        { name: 'version', type: 'number' },
        'timestamp',
        {
          // needs to select a filter
          title: 'Filter',
          name: 'filter',
          fields: [
            {
              name: 'text',
              fields: ['field', { name: 'value', type: 'string[]' }, 'operator'],
            },
            {
              name: 'numeric',
              fields: ['field', { name: 'value', type: 'number' }, 'operator'],
            },
            {
              name: 'boolean',
              fields: ['field', { name: 'value', fieldType: FieldType.toggle }, 'operator'],
            },
          ],
        },
      ],
    },
  ],
  [
    RawDataLocationType.csvFolder,
    {
      name: 'csvFolder',
      fields: [
        { name: 'rootFolder', info: 'example: s3a://feature-store-test-data/csv_folder' },
        'filterPattern',
        'delimiter',
      ],
    },
  ],
  [
    RawDataLocationType.parquetFolder,
    {
      name: 'parquetFolder',
      fields: [{ name: 'rootFolder', info: 'example: s3a://feature-store-test-data/data_folder' }, 'filterPattern'],
    },
  ],
  [
    RawDataLocationType.jsonFolder,
    {
      name: 'jsonFolder',
      fields: [
        { name: 'rootFolder', info: 'example: s3a://feature-store-test-data/json_folder' },
        'filterPattern',
        { name: 'multiline', fieldType: FieldType.toggle },
      ],
    },
  ],
]);

const deltaTableFilterOptions: IDropdownOption[] = [
  { key: DeltaTableFilterType.noFilter, text: DeltaTableFilterType.noFilter },
  { key: DeltaTableFilterType.text, text: DeltaTableFilterType.text },
  { key: DeltaTableFilterType.numeric, text: DeltaTableFilterType.numeric },
  { key: DeltaTableFilterType.boolean, text: DeltaTableFilterType.boolean },
];

const azureCredentialTypeOptions: IDropdownOption[] = [
  { key: AzureCredentialType.key, text: AzureCredentialType.key },
  { key: AzureCredentialType.SAS, text: AzureCredentialType.SAS },
  { key: AzureCredentialType.serviceProvider, text: AzureCredentialType.serviceProvider },
];

const dataTypeOptions: IDropdownOption[] = [
  { key: RawDataLocationType.csv, text: RawDataLocationType.csv },
  { key: RawDataLocationType.parquet, text: RawDataLocationType.parquet },
  { key: RawDataLocationType.snowflake, text: RawDataLocationType.snowflake },
  { key: RawDataLocationType.jdbc, text: RawDataLocationType.jdbc },
  { key: RawDataLocationType.json, text: RawDataLocationType.json },
  { key: RawDataLocationType.deltaTable, text: RawDataLocationType.deltaTable },
  { key: RawDataLocationType.csvFolder, text: RawDataLocationType.csvFolder },
  { key: RawDataLocationType.parquetFolder, text: RawDataLocationType.parquetFolder },
  { key: RawDataLocationType.jsonFolder, text: RawDataLocationType.jsonFolder },
];

const getDataTypeOptions = (hasTransformation?: boolean) =>
  hasTransformation
    ? [
        ...dataTypeOptions,
        { key: RawDataLocationType.join, text: RawDataLocationType.join },
        { key: RawDataLocationType.sparkpipelineTransformation, text: RawDataLocationType.sparkpipelineTransformation },
        { key: RawDataLocationType.mojoTransformation, text: RawDataLocationType.mojoTransformation },
      ]
    : dataTypeOptions;

const getSourceTypeOptions = (dataType?: RawDataLocationType): IDropdownOption[] => {
  switch (dataType) {
    case RawDataLocationType.jdbc:
      return [{ key: CredentialType.jdbcDatabase, text: CredentialType.jdbcDatabase }];
    case RawDataLocationType.snowflake:
      return [{ key: CredentialType.snowflake, text: CredentialType.snowflake }];
    default:
      return [
        { key: CredentialType.aws, text: CredentialType.aws },
        { key: CredentialType.minio, text: CredentialType.minio },
        { key: CredentialType.azure, text: CredentialType.azure },
        { key: CredentialType.drive, text: CredentialType.drive },
      ];
  }
};

const updateValue = (
  parents: string[],
  nameOrInfo: string | { [key: string]: any },
  values: any,
  updateValues: (values: DataSourceInfo) => void,
  name: string,
  value?: any
) => {
  if (typeof nameOrInfo !== 'string') {
    if (nameOrInfo.type === 'number') {
      value = value?.trim() ? +value : undefined;
    } else if (nameOrInfo.type === 'string[]') {
      value = value?.trim().split(' ');
    }
  }
  const lastParentIndex = parents.length - 1;
  const newValues = { ...values };
  let parent = newValues;
  parents.forEach((parentName, index) => {
    if (!parent[parentName]) {
      parent[parentName] = {};
    }
    parent = parent[parentName];
    if (index === lastParentIndex) {
      parent[name] = value;
    }
  });
  updateValues({ ...newValues });
};

const getFileExtensions = (fieldName: string) => {
  switch (fieldName) {
    case 'csv':
    case 'csvFolder':
      return ['csv'];
    case 'json':
    case 'jsonFolder':
      return ['json'];
    case 'parquet':
    case 'parquetFolder':
      return ['parquet'];
  }
  return undefined;
};

export const renderFieldLabelAndInfo = (label: string, info: string) => (
  <div style={{ display: 'flex', lineHeight: '18px', height: '16px', marginBottom: '8px' }}>
    <div style={{ marginRight: 8 }}>{label}</div>
    <Info styles={infoStylesNoSpace} isTooltip>
      {info}
    </Info>
  </div>
);

const getFieldLabel = (name: string, _info?: string) => {
  let label = '';
  switch (name) {
    case 'url':
      label = 'URL';
      break;
    default:
      label = camelCaseToTitleCase(name);
      break;
  }
  return label;
};

const renderField = (
  parents: string[],
  nameOrInfo: string | { [key: string]: any },
  values: DataSourceInfo,
  updateValues: (values: DataSourceInfo) => void,
  changedFieldValues?: ChangedFieldValue,
  onChangeFieldValues?: (changedFieldType: ChangedFieldType, values?: ChangedFieldValue) => void,
  hasUploadFile?: boolean
) => {
  const isString = typeof nameOrInfo === 'string';
  const fieldType = !isString && nameOrInfo.fieldType ? nameOrInfo.fieldType : '';
  if (
    !fieldType &&
    (isString ||
      !nameOrInfo.type ||
      nameOrInfo.type === 'number' ||
      nameOrInfo.type === 'string[]' ||
      nameOrInfo.type === 'password')
  ) {
    const name = isString ? nameOrInfo : nameOrInfo.name;
    const info = isString ? '' : nameOrInfo.info;
    const type = isString || !nameOrInfo.type || nameOrInfo.type === 'string[]' ? 'text' : nameOrInfo.type;
    const multiline = !isString && nameOrInfo.multiline;
    const key = [...parents, name].join('-');
    const parentName = parents[parents.length - 1];
    const fieldName = dataSourceUploadFileFieldMap.get(parentName);
    const hasUploadFileField = hasUploadFile && fieldName && fieldName === name;
    return (
      <div key={key}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <TextField
            styles={{ root: { flex: 1 } }}
            name={name}
            label={getFieldLabel(name)}
            onRenderLabel={info ? () => renderFieldLabelAndInfo(getFieldLabel(name), info) : undefined}
            type={type}
            multiline={multiline}
            canRevealPassword={type === 'password'}
            onChange={(_e, value) => updateValue(parents, nameOrInfo, values, updateValues, name, value)}
          />
          {hasUploadFileField ? (
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
              <Link
                styles={[linkStylesGhost, { root: { height: '32px' } }]}
                onClick={() => onChangeFieldValues && onChangeFieldValues(ChangedFieldType.showFileUpload)}
              >
                Upload
              </Link>
            </div>
          ) : null}
        </div>
        {hasUploadFileField && changedFieldValues?.showFileUpload ? (
          <FileUpload
            uploadCallback={(files: File[]) => {
              onChangeFieldValues &&
                onChangeFieldValues(ChangedFieldType.files, { files, parents: [...parents], name });
            }}
            styles={{ root: { marginTop: '24px' } }}
            maxFileSize={200}
            maxSize={200}
            multiple={fieldName !== 'path'}
            label="Maximum size: 200MB"
            placeholder=""
            data-test="upload-select-data-source"
            fileExtensions={getFileExtensions(parentName)}
          />
        ) : null}
      </div>
    );
  } else if (fieldType === FieldType.toggle) {
    if (!isString) {
      const name = nameOrInfo.name;
      const info = nameOrInfo.info;
      const key = [...parents, name].join('-');
      return (
        <Toggle
          styles={toggleStylesSelectDataSource}
          id={name}
          key={key}
          label={info ? renderFieldLabelAndInfo(getFieldLabel(name), info) : getFieldLabel(name)}
          inlineLabel
          onChange={(_e, value) => updateValue(parents, nameOrInfo, values, updateValues, name, value)}
        />
      );
    }
  }
  return null;
};

const renderAzureCredentialFields = (
  type: AzureCredentialType,
  fields: any[],
  values: DataSourceInfo,
  updateValues: (values: DataSourceInfo) => void
) => {
  switch (type) {
    case AzureCredentialType.key:
      return fields[0].fields.map((nameOrInfo: any) =>
        renderField(['cred', 'azure'], nameOrInfo, values, updateValues)
      );
    case AzureCredentialType.SAS:
      return fields[1].fields.map((nameOrInfo: any) =>
        renderField(['cred', 'azure'], nameOrInfo, values, updateValues)
      );
    case AzureCredentialType.serviceProvider:
      return fields[2].fields.map((nameOrInfo: any) =>
        renderField(['cred', 'azure'], nameOrInfo, values, updateValues)
      );
  }
};

const renderCredentialFields = (
  type: CredentialType | undefined,
  values: DataSourceInfo,
  updateValues: (values: DataSourceInfo) => void,
  classNames: ISelectDataSourceClassNames,
  azureCredentialType: AzureCredentialType | undefined,
  setAzureCredentialType: React.Dispatch<React.SetStateAction<AzureCredentialType | undefined>>,
  snowflakeUsePrivateKey: boolean | undefined,
  setSnowflakeUsePrivateKey: React.Dispatch<React.SetStateAction<boolean | undefined>>
) => {
  if (!type) {
    return null;
  }
  const info = credentialTypeInfoMap.get(type);
  return (
    <Fragment key="credential-fields">
      {info?.fields.map((nameOrInfo: string | { [key: string]: any }) => {
        if (typeof nameOrInfo !== 'string' && nameOrInfo.title) {
          switch (nameOrInfo.title) {
            case 'Azure credentials':
              return (
                <Fragment key="credential-fields-dropdown-types">
                  {renderSubTitle(nameOrInfo.title, classNames.subTitle)}
                  <Dropdown
                    key="azureCredentialTypeDropdown"
                    label="What Azure credentials to use?"
                    placeholder="Select Credentials Type"
                    options={azureCredentialTypeOptions}
                    selectedKey={azureCredentialType}
                    onChange={(_e, option) => {
                      if (option) {
                        setAzureCredentialType(option.key as AzureCredentialType);
                      }
                    }}
                  />
                  {azureCredentialType
                    ? renderAzureCredentialFields(azureCredentialType, nameOrInfo.fields, values, updateValues)
                    : null}
                </Fragment>
              );
            case 'Snowflake Use Private Key':
              return (
                <Fragment key="credential-fields-toggle-private-key">
                  <Toggle
                    styles={toggleStylesSelectDataSource}
                    key={nameOrInfo.title}
                    label="Use Private Key"
                    inlineLabel
                    onChange={(_e, value) => {
                      setSnowflakeUsePrivateKey(value);
                    }}
                  />
                  {snowflakeUsePrivateKey &&
                    nameOrInfo.fields.map((field: any) =>
                      renderField(['cred', 'snowflake'], field, values, updateValues)
                    )}
                </Fragment>
              );
          }
        }
        return renderField(['cred', info.name as string], nameOrInfo, values, updateValues);
      })}
    </Fragment>
  );
};

const renderSubTitle = (title: string, className: string) => {
  return (
    <div className={className} key={title}>
      {title}
    </div>
  );
};

const renderDeltaTableFilterFields = (
  filterType: DeltaTableFilterType,
  fields: any[],
  values: DataSourceInfo,
  updateValues: (values: DataSourceInfo) => void
) => {
  switch (filterType) {
    case DeltaTableFilterType.text:
      return fields[0].fields.map((nameOrInfo: any) =>
        renderField(['dataSource', 'deltaTable', 'filter', 'text'], nameOrInfo, values, updateValues)
      );
    case DeltaTableFilterType.numeric:
      return fields[1].fields.map((nameOrInfo: any) =>
        renderField(['dataSource', 'deltaTable', 'filter', 'numeric'], nameOrInfo, values, updateValues)
      );
    case DeltaTableFilterType.boolean:
      return fields[2].fields.map((nameOrInfo: any) =>
        renderField(['dataSource', 'deltaTable', 'filter', 'boolean'], nameOrInfo, values, updateValues)
      );
  }
};

const rawDataLocationTypeTransformationTypeMap = new Map<RawDataLocationType, TransformationType>([
  [RawDataLocationType.sparkpipelineTransformation, TransformationType.TransformationSparkPipeline],
  [RawDataLocationType.mojoTransformation, TransformationType.TransformationMojo],
]);

const isDerivedFromDataType = (dataType?: RawDataLocationType) =>
  !dataType
    ? false
    : [
        RawDataLocationType.join,
        RawDataLocationType.sparkpipelineTransformation,
        RawDataLocationType.mojoTransformation,
      ].includes(dataType);

const hasValidDerivedInformation = (
  dataType?: RawDataLocationType,
  { derivedFrom, tempTransformationInfo }: DataSourceInfo = {}
): boolean => {
  if (!isDerivedFromDataType(dataType)) {
    return true;
  }
  // join
  if (dataType === RawDataLocationType.join) {
    if (derivedFrom) {
      const { featureSetIds, transformation } = derivedFrom;
      return (
        featureSetIds?.length === 2 &&
        !featureSetIds.some((versionId) => !versionId.id) &&
        !!transformation?.join?.leftKey &&
        !!transformation?.join?.rightKey
      );
    }
  } else {
    if (tempTransformationInfo) {
      // Spark pipeline or mojo
      const { featureSetIdVersion, file } = tempTransformationInfo;
      return !!featureSetIdVersion?.id && !!file;
    }
  }
  return false;
};

const renderRawDataLocationFields = (
  type: RawDataLocationType | undefined,
  values: DataSourceInfo,
  updateValues: (values: DataSourceInfo) => void,
  classNames: ISelectDataSourceClassNames,
  changedFieldValues: ChangedFieldValue,
  onChangeFieldValues: (changedFieldType: ChangedFieldType, values?: ChangedFieldValue) => void,
  hasUploadFile?: boolean
) => {
  if (!type) {
    return null;
  }
  const info = rawDataLocationTypeInfoMap.get(type);
  return (
    <Fragment key="raw-data-location-type-filter-title-fields">
      {info?.fields.map((nameOrInfo: string | { [key: string]: any }) => {
        if (typeof nameOrInfo !== 'string' && nameOrInfo.title) {
          if (nameOrInfo.title === 'Filter') {
            const deltaTableFilter = changedFieldValues.deltaTableFilter;
            return (
              <Fragment key="raw-data-location-type-filter-title-fields-title-dropdown-fields">
                {renderSubTitle(nameOrInfo.title, classNames.subTitle)}
                <Dropdown
                  key="deltaTableFilterTypeDropdown"
                  label="Select Filter Type"
                  placeholder="Select Filter Type"
                  options={deltaTableFilterOptions}
                  selectedKey={deltaTableFilter}
                  onChange={(_e, option) => {
                    if (option) {
                      onChangeFieldValues(ChangedFieldType.deltaTableFilter, {
                        deltaTableFilter: option.key as DeltaTableFilterType,
                      });
                    }
                  }}
                />
                {deltaTableFilter && deltaTableFilter !== DeltaTableFilterType.noFilter
                  ? renderDeltaTableFilterFields(deltaTableFilter, nameOrInfo.fields, values, updateValues)
                  : null}
              </Fragment>
            );
          }
          return (
            <Fragment key="raw-data-location-type-source-title-fields">
              {renderSubTitle(nameOrInfo.title, classNames.subTitle)}
              {nameOrInfo.fields.map((subFieldInfo: string | { [key: string]: any }) => {
                return renderField(['dataSource', info.name, nameOrInfo.name], subFieldInfo, values, updateValues);
              })}
            </Fragment>
          );
        }
        return renderField(
          ['dataSource', info.name],
          nameOrInfo,
          values,
          updateValues,
          changedFieldValues,
          onChangeFieldValues,
          hasUploadFile
        );
      })}
    </Fragment>
  );
};

const renderDerivedFromFields = (
  type: RawDataLocationType | undefined,
  updateValues: (values: DataSourceInfo) => void
) => {
  switch (type) {
    case RawDataLocationType.join:
      return <FeatureSetJoin onChange={(derivedFrom) => updateValues({ derivedFrom })} />;
    case RawDataLocationType.sparkpipelineTransformation:
    case RawDataLocationType.mojoTransformation:
      return (
        <FeatureSetTransformation
          transformationType={rawDataLocationTypeTransformationTypeMap.get(type)}
          onChange={(tempTransformationInfo) => {
            updateValues({ tempTransformationInfo });
          }}
        />
      );
    default:
      return null;
  }
};

const applyDefaultValuesToAws = (values: DataSourceInfo) => {
  if (values.cred && values.cred.aws && !values.cred.aws.accessToken && !values.cred.aws.secretToken) {
    values.cred.aws.accessToken = 'PUBLIC_ONLY';
    values.cred.aws.secretToken = 'PUBLIC_ONLY';
  }
  return values;
};

interface ISelectDataSourceProps {
  hasTransformation?: boolean;
  hasUploadFile?: boolean;
  dataSourceInfo?: DataSourceInfo;
  onChange: (values: DataSourceInfo, hasValidDerivedInformation: boolean) => void;
}

export const SelectDataSource = ({
  hasTransformation,
  hasUploadFile,
  dataSourceInfo = getEmptyValues(),
  onChange,
}: ISelectDataSourceProps) => {
  const [sourceType, setSourceType] = useState<CredentialType>();
  const [dataType, setDataType] = useState<RawDataLocationType>();
  const [deltaTableFilter, setDeltaTableFilter] = useState<DeltaTableFilterType>();
  const [azureCredentialType, setAzureCredentialType] = useState<AzureCredentialType>();
  const [snowflakeUsePrivateKey, setSnowflakeUsePrivateKey] = useState<boolean>();
  const [showFileUpload, setShowFileUpload] = useState<boolean>();
  const [values, setValues] = useState<DataSourceInfo>({ ...dataSourceInfo });
  const updateValues = useCallback(
    (values: DataSourceInfo, targetDataType?: RawDataLocationType) => {
      const newValues: DataSourceInfo = {
        ...values,
        tempDataSourceFileInfo: !showFileUpload ? undefined : values.tempDataSourceFileInfo,
      };
      applyDefaultValuesToAws(newValues);
      setValues(newValues);
      onChange({ ...newValues }, hasValidDerivedInformation(targetDataType || dataType, newValues));
    },
    [onChange, dataType]
  );
  const onChangeFieldValues = useCallback(
    (changedFieldType: ChangedFieldType, value?: ChangedFieldValue) => {
      switch (changedFieldType) {
        case ChangedFieldType.deltaTableFilter:
          setDeltaTableFilter(value?.deltaTableFilter);
          break;
        case ChangedFieldType.showFileUpload:
          setShowFileUpload(!showFileUpload);
          break;
        case ChangedFieldType.files:
          const tempDataSourceFileInfo: DataSourceFileInfo = {
            parents: value?.parents,
            fileMd5s: [],
            name: value?.name,
          };
          const fileCount = value?.files?.length || 0;
          let loadEndCount = 0;
          value?.files?.forEach((file) => {
            const reader = new FileReader();
            reader.onloadend = (e) => {
              loadEndCount++;
              const md5Checksum = getMd5Checksum(e);
              if (md5Checksum) {
                tempDataSourceFileInfo.fileMd5s?.push({ md5Checksum, file });
              }
              if (fileCount === loadEndCount) {
                const newValues = { ...values, tempDataSourceFileInfo };
                setValues(newValues);
                onChange({ ...newValues }, true);
              }
            };
            reader.readAsArrayBuffer(file);
          });
          break;
      }
    },
    [values, onChange, showFileUpload]
  );

  const classNames = useClassNames<ISelectDataSourceStyles, ISelectDataSourceClassNames>(
    'SelectDataSource',
    selectDataSourceStylesDefault
  );
  return (
    <div className={classNames.root} data-test="select-data-source">
      <div className={classNames.subTitle} key="data-type">
        Data Type
      </div>
      <Dropdown
        key="source"
        placeholder="Select Data Type"
        options={getDataTypeOptions(hasTransformation)}
        onChange={(_e, option) => {
          if (option) {
            const t = option.key as RawDataLocationType;
            setDataType(t);
            switch (t) {
              case RawDataLocationType.jdbc:
                setSourceType(CredentialType.jdbcDatabase);
                break;
              case RawDataLocationType.snowflake:
                setSourceType(CredentialType.snowflake);
                break;
              default:
                setSourceType(undefined);
            }
            const newValues: DataSourceInfo = {
              ...values,
              derivedFrom: undefined,
              tempTransformationInfo:
                t === RawDataLocationType.mojoTransformation || t === RawDataLocationType.sparkpipelineTransformation
                  ? { ...values.tempTransformationInfo }
                  : undefined,
              tempDataSourceFileInfo: undefined,
            };
            setShowFileUpload(false);
            updateValues(newValues, t);
          }
        }}
      />
      {dataType
        ? isDerivedFromDataType(dataType)
          ? renderDerivedFromFields(dataType, updateValues)
          : renderRawDataLocationFields(
              dataType,
              values,
              updateValues,
              classNames,
              { deltaTableFilter, showFileUpload },
              onChangeFieldValues,
              hasUploadFile
            )
        : null}
      {!isDerivedFromDataType(dataType) && (
        <div className={classNames.subTitle} key="source-type">
          {sourceType !== CredentialType.jdbcDatabase && sourceType !== CredentialType.snowflake
            ? 'Cloud Storage Provider'
            : 'Credentials'}
        </div>
      )}
      {!isDerivedFromDataType(dataType) &&
        sourceType !== CredentialType.jdbcDatabase &&
        sourceType !== CredentialType.snowflake && (
          <Dropdown
            key="cred"
            placeholder="Select Cloud Storage Provider"
            selectedKey={sourceType || ''}
            options={getSourceTypeOptions(dataType)}
            onChange={(_e, option) => {
              if (option) {
                const credType = option.key as CredentialType;
                setSourceType(credType);
                const cred: { [key: string]: any } = {};
                cred[credentialTypeFieldNameMap.get(credType) as string] = {};
                updateValues({ ...values, cred });
              }
            }}
          />
        )}
      {sourceType &&
        renderCredentialFields(
          sourceType,
          values,
          updateValues,
          classNames,
          azureCredentialType,
          setAzureCredentialType,
          snowflakeUsePrivateKey,
          setSnowflakeUsePrivateKey
        )}
    </div>
  );
};
